import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Ticket as TicketIcon, ShoppingCart, Trash2, ChevronRight, Check, User, Star } from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";

/**
 * Öffentlicher Shop-Warenkorb:
 * - Geldgutscheine (5–500 €)
 * - 10er-Kinokarten (3–30 Eintritte à Preis pro Ticket)
 * - Einheitliches Checkout mit E-Mail-Empfänger
 * - Bezahlung: „an der Kasse zahlen" (Reservierung), tatsächliche Zahlung/Stripe kommt später
 */

const VOUCHER_PRESETS = [15, 25, 50, 75, 100];
const PACK_PRESETS = [
  { count: 5,  price: 12 },
  { count: 10, price: 11 },
  { count: 15, price: 10 },
];

export default function Shop() {
  const [cart, setCart] = useState([]);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [payMethod, setPayMethod] = useState("online"); // "online" | "register"
  const [busy, setBusy] = useState(false);
  const [orderResult, setOrderResult] = useState(null); // { codes: [...] }
  const [defaultTicketPrice, setDefaultTicketPrice] = useState(12);

  useEffect(() => {
    api.get("/public/pricing").then((r) => {
      const p = r.data?.default_ticket_price;
      if (p) setDefaultTicketPrice(p);
    }).catch(() => { /* ignore, use fallback */ });
  }, []);

  const addVoucher = (amount) => {
    setCart((c) => [...c, {
      id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: "voucher",
      amount,
      recipient_name: "",
      from_name: "",
      message: "",
      theme: "classic",
    }]);
    toast.success(`${amount} € Gutschein hinzugefügt`);
  };

  const addPack = (count, pricePerTicket) => {
    setCart((c) => [...c, {
      id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: "pack",
      ticket_count: count,
      price_per_ticket: pricePerTicket,
      recipient_name: "",
      from_name: "",
    }]);
    toast.success(`${count}er-Kinokarte hinzugefügt`);
  };

  const removeItem = (id) => setCart((c) => c.filter((x) => x.id !== id));
  const patchItem = (id, patch) => setCart((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const itemTotal = (item) => item.kind === "voucher" ? Number(item.amount || 0) : Number(item.ticket_count) * Number(item.price_per_ticket);
  const total = cart.reduce((s, it) => s + itemTotal(it), 0);

  const checkout = async () => {
    if (!cart.length) return toast.error("Warenkorb ist leer");
    if (!buyer.email.trim() || !buyer.email.includes("@")) return toast.error("Bitte gültige E-Mail eingeben");
    if (!buyer.name.trim()) return toast.error("Bitte Name eingeben");
    for (const it of cart) {
      if (!it.recipient_name?.trim()) {
        return toast.error("Bitte für jeden Artikel einen Empfängernamen angeben");
      }
    }
    setBusy(true);
    // Online-Zahlung via Stripe: alle Artikel in einer Session
    if (payMethod === "online") {
      try {
        const items = cart.map((it) => ({
          type: it.kind,
          payload: it.kind === "voucher"
            ? { amount: Number(it.amount), recipient_name: it.recipient_name.trim(), from_name: (it.from_name || buyer.name).trim(), message: it.message || "", email: buyer.email.trim(), theme: it.theme || "classic" }
            : { ticket_count: Number(it.ticket_count), price_per_ticket: Number(it.price_per_ticket), recipient_name: it.recipient_name.trim(), from_name: (it.from_name || buyer.name).trim(), email: buyer.email.trim(), theme: "classic" },
        }));
        const { data } = await api.post("/payments/checkout", {
          items,
          origin_url: window.location.origin,
          customer_email: buyer.email.trim(),
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error("Konnte Stripe-Checkout nicht öffnen");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Online-Zahlung nicht verfügbar — bitte an der Kasse zahlen.");
      } finally { setBusy(false); }
      return;
    }
    // Bar an der Kasse — bestehender Flow
    const codes = [];
    try {
      for (const it of cart) {
        if (it.kind === "voucher") {
          const { data } = await api.post("/vouchers/order", {
            amount: Number(it.amount),
            recipient_name: it.recipient_name.trim(),
            from_name: (it.from_name || buyer.name).trim(),
            message: it.message || "",
            email: buyer.email.trim(),
            theme: it.theme || "classic",
          });
          codes.push({ code: data.code, kind: "voucher", amount: Number(it.amount), recipient: it.recipient_name });
        } else {
          const { data } = await api.post("/ticket-packs/order", {
            ticket_count: Number(it.ticket_count),
            price_per_ticket: Number(it.price_per_ticket),
            recipient_name: it.recipient_name.trim(),
            from_name: (it.from_name || buyer.name).trim(),
            email: buyer.email.trim(),
          });
          codes.push({ code: data.code, kind: "pack", count: Number(it.ticket_count), recipient: it.recipient_name });
        }
      }
      setOrderResult({ codes });
      setCart([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bestellung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (orderResult) return <OrderSuccess result={orderResult} email={buyer.email} onReset={() => { setOrderResult(null); setBuyer({ name: "", email: "", phone: "" }); }} />;

  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F5F0E6]">
      {/* Nav */}
      <nav className="border-b border-[#1A1A1A] px-6 md:px-14 py-6 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/"><CinemaBrand size="sm" /></Link>
        <div className="flex items-center gap-4">
          <Link to="/program" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Programm</Link>
          <Link to="/mitglied/login" className="text-[10px] uppercase tracking-[0.4em] text-[#EEDC82] hover:text-[#D4AF37]">
            <User size={11} className="inline mr-1" /> Mitglied
          </Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Movie House · Shop</div>
          <h1 className="font-heading text-4xl md:text-5xl mt-3">Gutscheine, Sparabo & Kartenpakete</h1>
          <p className="text-[#A8A398] mt-3 max-w-xl mx-auto">Schenke Kinoerlebnisse. Zahle bequem an der Kasse beim ersten Besuch oder überweise vorab — wir schicken dir alle Codes per E-Mail.</p>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* Katalog */}
          <div className="space-y-8">
            {/* Geldgutscheine */}
            <section className="card-lux p-6" data-testid="shop-voucher-section">
              <div className="flex items-center gap-3 mb-4">
                <Gift size={20} className="text-[#D4AF37]" />
                <h2 className="font-heading text-2xl text-[#D4AF37]">Geldgutschein</h2>
              </div>
              <p className="text-sm text-[#A8A398] mb-5">Wert wählen. Einlösbar für Tickets, Snacks und Getränke. 12 Monate gültig.</p>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {VOUCHER_PRESETS.map((v) => (
                  <button key={v} onClick={() => addVoucher(v)} data-testid={`shop-add-voucher-${v}`}
                    className="border border-[#2A2A2A] rounded-md py-4 font-heading text-2xl text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transitions-safe">
                    {v}<span className="text-sm ml-0.5">€</span>
                  </button>
                ))}
                <CustomVoucherAmount onAdd={addVoucher} />
              </div>
            </section>

            {/* 10er-Kinokarten (Sparabo) */}
            <section className="card-lux p-6" data-testid="shop-pack-section">
              <div className="flex items-center gap-3 mb-4">
                <TicketIcon size={20} className="text-[#D4AF37]" />
                <h2 className="font-heading text-2xl text-[#D4AF37]">Sparabo · Kinokarten-Pakete</h2>
              </div>
              <p className="text-sm text-[#A8A398] mb-5">Vergünstigte Tickets im Paket. 24 Monate gültig, ideal für Vielseher & als Geschenk. Regulärer Preis: {defaultTicketPrice} €.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PACK_PRESETS.map((p) => {
                  const save = (defaultTicketPrice - p.price) * p.count;
                  const total = p.price * p.count;
                  return (
                    <button key={p.count} onClick={() => addPack(p.count, p.price)} data-testid={`shop-add-pack-${p.count}`}
                      className="text-left border border-[#2A2A2A] rounded-md p-4 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transitions-safe">
                      <div className="font-heading text-3xl text-[#D4AF37]">{p.count}<span className="text-sm text-[#A8A398] ml-1">Eintritte</span></div>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <span className="text-xl font-mono">{total}</span>
                        <span className="text-xs text-[#A8A398]">€ ({p.price} €/Ticket)</span>
                      </div>
                      {save > 0 && <div className="mt-1 text-[10px] uppercase tracking-widest text-[#C0392B]">Du sparst {save} €</div>}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Filmtickets Link */}
            <section className="card-lux p-6 flex items-center justify-between gap-4" data-testid="shop-tickets-link">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <TicketIcon size={20} className="text-[#D4AF37]" />
                  <h2 className="font-heading text-2xl text-[#D4AF37]">Einzeltickets für Filme</h2>
                </div>
                <p className="text-sm text-[#A8A398]">Wähle Datum, Sitzplatz und Snack — Tickets werden direkt reserviert.</p>
              </div>
              <Link to="/program" data-testid="shop-goto-program"
                className="whitespace-nowrap bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-3 px-5 rounded font-medium text-[11px] uppercase tracking-[0.28em] flex items-center gap-2">
                Zum Programm <ChevronRight size={16} />
              </Link>
            </section>
          </div>

          {/* Warenkorb */}
          <aside className="lg:sticky lg:top-6 h-fit">
            <div className="card-lux p-5" data-testid="shop-cart">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart size={18} className="text-[#D4AF37]" />
                <h3 className="font-heading text-xl text-[#D4AF37]">Warenkorb</h3>
                {cart.length > 0 && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#A8A398]">{cart.length} Artikel</span>}
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-8 text-sm text-[#575148]" data-testid="shop-cart-empty">
                  Noch nichts gewählt.<br />Wähle links einen Gutschein oder ein Sparabo.
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  {cart.map((it) => (
                    <div key={it.id} className="border border-[#1F1F1F] rounded p-3 space-y-2" data-testid={`cart-item-${it.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                          {it.kind === "voucher" ? `${it.amount} € Gutschein` : `${it.ticket_count}er-Karte`}
                        </div>
                        <button onClick={() => removeItem(it.id)} data-testid={`cart-remove-${it.id}`}
                          className="text-[#575148] hover:text-[#EE9CA1]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <input type="text" placeholder="Für wen? (Empfänger)" value={it.recipient_name}
                        onChange={(e) => patchItem(it.id, { recipient_name: e.target.value })}
                        data-testid={`cart-recipient-${it.id}`}
                        className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm focus:border-[#D4AF37] outline-none" />
                      {it.kind === "voucher" && (
                        <input type="text" placeholder="Persönliche Nachricht (optional)" value={it.message}
                          onChange={(e) => patchItem(it.id, { message: e.target.value })}
                          className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs focus:border-[#D4AF37] outline-none" />
                      )}
                      <div className="text-right text-sm font-mono text-[#D4AF37]">{itemTotal(it).toFixed(2)} €</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Käufer-Info */}
              {cart.length > 0 && (
                <div className="space-y-2 border-t border-[#1A1A1A] pt-4 mb-4">
                  <input placeholder="Dein Name" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                    data-testid="shop-buyer-name"
                    className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                  <input type="email" placeholder="E-Mail (Codes werden gesendet)" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                    data-testid="shop-buyer-email"
                    className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              )}

              {/* Summe + Zahlweise + Checkout */}
              <div className="border-t border-[#1A1A1A] pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="uppercase tracking-widest text-[10px] text-[#A8A398]">Summe</span>
                  <span className="font-heading text-2xl text-[#D4AF37]" data-testid="cart-total">{total.toFixed(2)} €</span>
                </div>

                {/* Zahlweise-Toggle */}
                {cart.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-2" data-testid="shop-pay-method">
                    <button type="button" onClick={() => setPayMethod("online")}
                      data-testid="shop-pay-online"
                      className={`text-left rounded p-3 border-2 transitions-safe ${payMethod === "online" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]/50"}`}>
                      <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]">💳 Online</div>
                      <div className="text-xs mt-1 text-[#F5F0E6]">Stripe · Karte / Apple Pay</div>
                    </button>
                    <button type="button" onClick={() => setPayMethod("register")}
                      data-testid="shop-pay-register"
                      className={`text-left rounded p-3 border-2 transitions-safe ${payMethod === "register" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]/50"}`}>
                      <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]">💰 An Kasse</div>
                      <div className="text-xs mt-1 text-[#F5F0E6]">Beim ersten Besuch</div>
                    </button>
                  </div>
                )}

                <button onClick={checkout} disabled={busy || !cart.length} data-testid="shop-checkout-btn"
                  className="w-full py-3 bg-[#D4AF37] hover:bg-[#EEDC82] disabled:opacity-40 text-black font-medium tracking-widest uppercase text-[11px] rounded">
                  {busy ? "…" : (payMethod === "online" ? "Zur Bezahlung · Stripe" : "Bestellen · An Kasse zahlen")}
                </button>
                <div className="text-[10px] text-[#575148] text-center leading-relaxed">
                  {payMethod === "online"
                    ? <>Sichere Zahlung via Stripe. Nach Bestätigung landet der Code direkt in deinem Postfach.</>
                    : <>Bezahlung beim ersten Besuch an der Kinokasse.<br />Codes werden sofort per E-Mail zugesandt.</>}
                </div>
              </div>
            </div>

            {/* Mitglieds-Hinweis */}
            <div className="mt-4 border border-[#8B7328]/40 bg-[#D4AF37]/[0.04] rounded p-4 text-center">
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#D4AF37]"><Star size={11} className="inline mr-1" fill="#D4AF37" strokeWidth={0} /> Movie House Club</div>
              <div className="text-xs text-[#A8A398] mt-1.5">Mitglieder erhalten reduzierte Preise & Vorab-Zugriff auf Premieren.</div>
              <Link to="/mitglied/login" data-testid="shop-member-cta" className="mt-2 inline-block text-[10px] uppercase tracking-[0.28em] text-[#EEDC82] hover:text-[#D4AF37] underline">
                Zum Mitglieder-Login →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function CustomVoucherAmount({ onAdd }) {
  const [v, setV] = useState("");
  const submit = () => {
    const n = parseFloat(v);
    if (!n || n < 5 || n > 500) return toast.error("Wert zwischen 5 € und 500 €");
    onAdd(n);
    setV("");
  };
  return (
    <div className="flex flex-col border border-dashed border-[#2A2A2A] rounded-md hover:border-[#D4AF37]/60 transitions-safe overflow-hidden">
      <input type="number" min={5} max={500} step={5} value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="frei" data-testid="shop-voucher-custom-input"
        className="flex-1 bg-transparent text-center py-2 outline-none font-heading text-lg text-[#D4AF37]" />
      <button onClick={submit} data-testid="shop-voucher-custom-add"
        className="text-[9px] uppercase tracking-widest bg-[#1A1A1A] hover:bg-[#D4AF37] hover:text-black py-1">
        + Hinzufügen
      </button>
    </div>
  );
}

function OrderSuccess({ result, email, onReset }) {
  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F5F0E6] flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center" data-testid="shop-order-success">
        <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
          <Check size={40} className="text-[#D4AF37]" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Bestellt</div>
        <h1 className="font-heading text-4xl mt-3">Danke für deine Bestellung!</h1>
        <p className="text-[#A8A398] mt-3">Alle Codes gingen soeben an <span className="text-[#EEDC82]">{email}</span>.<br />Bezahle den Betrag entspannt beim nächsten Besuch an der Kasse.</p>
        <div className="mt-8 card-lux p-5 text-left space-y-2">
          {result.codes.map((c, i) => (
            <div key={i} className="flex items-center justify-between border-b border-[#141414] last:border-0 pb-2 last:pb-0">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                  {c.kind === "voucher" ? `${c.amount} € Gutschein` : `${c.count}er-Karte`}
                </div>
                <div className="text-xs text-[#A8A398]">für {c.recipient}</div>
              </div>
              <div className="font-mono text-sm text-[#F5F0E6]">{c.code}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/" className="py-2 px-5 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">Zur Startseite</Link>
          <button onClick={onReset} data-testid="shop-order-again" className="py-2 px-5 bg-[#D4AF37] text-black rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]">Weiteren Artikel kaufen</button>
        </div>
      </div>
    </div>
  );
}
